import { useState, useRef, useEffect, useCallback } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { getImportanceBadgeClasses } from '@/utils/colors';
import type { Phase } from '@/types/api';

// --- Scoring config types ---

export interface PhaseConfig {
  phaseId: string;
  enabled: boolean;
  weight: number; // 0-1
  speed_intent?: 'match' | 'reward_faster' | 'reward_slower' | 'off';
}

// --- Component interfaces ---

interface PhaseEditorProps {
  videoId: string;
  videoUrl: string;
  fps: number;
  totalFrames: number;
  initialPhases?: Phase[];
  onPhasesChange: (phases: Phase[]) => void;
  onNext: () => void;
  onBack: () => void;
  // Scoring config props
  onConfigChange?: (configs: PhaseConfig[]) => void;
  initialConfigs?: PhaseConfig[];
  /** @deprecated Mirror-invariant scoring is now always enabled */
  mirrorInvariantScoring?: boolean;
  /** @deprecated Mirror-invariant scoring is now always enabled */
  onMirrorInvariantChange?: (enabled: boolean) => void;
}

interface PhaseMarker {
  id: string;
  name: string;
  startFrame: number;
  endFrame: number;
  importance: 'low' | 'medium' | 'high' | 'critical';
}

// --- Helpers ---

const defaultWeight = (importance: 'low' | 'medium' | 'high' | 'critical'): number => {
  switch (importance) {
    case 'critical': return 1;
    case 'high': return 0.8;
    case 'medium': return 0.5;
    default: return 0;  // low importance phases contribute nothing to overall score
  }
};

// --- Main Component ---

export function PhaseEditor({
  videoId: _videoId,
  videoUrl,
  fps,
  totalFrames,
  initialPhases = [],
  onPhasesChange,
  onNext,
  onBack,
  onConfigChange,
  initialConfigs,
  mirrorInvariantScoring: _mirrorInvariantScoring = false,
  onMirrorInvariantChange: _onMirrorInvariantChange,
}: PhaseEditorProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  // Flag to prevent feedback loops between video time and currentFrame state
  const isSettingTimeRef = useRef(false);

  const [phases, setPhases] = useState<PhaseMarker[]>(() => {
    if (initialPhases.length > 0) {
      return initialPhases.map(p => ({
        id: p.id || crypto.randomUUID(),
        // Handle both 'name' and 'label' fields (backend uses 'label', frontend uses 'name')
        name: p.name || (p as unknown as { label?: string }).label || `Phase ${initialPhases.indexOf(p) + 1}`,
        startFrame: p.start_frame,
        endFrame: p.end_frame,
        importance: p.importance || 'medium',
      }));
    }
    // Default: create 2 phases splitting the video in half
    const midFrame = Math.floor(totalFrames / 2);
    return [
      {
        id: crypto.randomUUID(),
        name: 'Phase 1',
        startFrame: 0,
        endFrame: midFrame - 1,
        importance: 'medium',
      },
      {
        id: crypto.randomUUID(),
        name: 'Phase 2',
        startFrame: midFrame,
        endFrame: totalFrames - 1,
        importance: 'medium',
      },
    ];
  });

  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(phases[0]?.id || null);
  const [draggedBoundary, setDraggedBoundary] = useState<{ phaseId: string; side: 'start' | 'end' } | null>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // --- Scoring config state ---

  const [phaseConfigs, setPhaseConfigs] = useState<PhaseConfig[]>(() => {
    if (initialConfigs) return initialConfigs;
    return phases.map(p => ({
      phaseId: p.id,
      enabled: true,
      weight: defaultWeight(p.importance),
    }));
  });

  // --- Phase sync to parent ---

  // Update parent when phases change
  // Note: onPhasesChange is intentionally excluded from deps to prevent infinite loops
  useEffect(() => {
    const apiPhases: Phase[] = phases.map(p => ({
      id: p.id,
      name: p.name,
      description: '',
      start_frame: p.startFrame,
      end_frame: p.endFrame,
      duration_frames: p.endFrame - p.startFrame + 1,
      importance: p.importance,
    }));
    onPhasesChange(apiPhases);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases]);

  // Notify parent of config changes
  useEffect(() => {
    onConfigChange?.(phaseConfigs);
  }, [phaseConfigs, onConfigChange]);

  // Sync configs when phases are added/removed
  useEffect(() => {
    setPhaseConfigs(prevConfigs => {
      const existingIds = new Set(prevConfigs.map(c => c.phaseId));
      const phaseIds = new Set(phases.map(p => p.id));

      // Add default config for new phases
      const newConfigs = phases
        .filter(p => !existingIds.has(p.id))
        .map(p => ({
          phaseId: p.id,
          enabled: true,
          weight: defaultWeight(p.importance),
        }));

      // Remove configs for deleted phases
      const filteredConfigs = prevConfigs.filter(c => phaseIds.has(c.phaseId));

      if (newConfigs.length === 0 && filteredConfigs.length === prevConfigs.length) {
        return prevConfigs; // No change
      }

      return [...filteredConfigs, ...newConfigs];
    });
   
  }, [phases]);

  // --- Video controls ---

  // Sync video current time with currentFrame (only when NOT playing)
  useEffect(() => {
    if (videoRef.current && !isPlaying) {
      isSettingTimeRef.current = true;
      videoRef.current.currentTime = currentFrame / fps;
      const timeoutId = setTimeout(() => {
        isSettingTimeRef.current = false;
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [currentFrame, fps, isPlaying]);

  // Update currentFrame when video plays
  useEffect(() => {
    if (!videoRef.current) return;

    const handleTimeUpdate = () => {
      if (isSettingTimeRef.current) return;
      if (videoRef.current) {
        const frame = Math.floor(videoRef.current.currentTime * fps);
        setCurrentFrame(Math.min(frame, totalFrames - 1));
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentFrame(0);
    };

    const video = videoRef.current;
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [fps, totalFrames]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const seekToFrame = (frame: number) => {
    setCurrentFrame(Math.max(0, Math.min(frame, totalFrames - 1)));
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // --- Timeline interactions ---

  const getFrameFromMouseEvent = useCallback((e: React.MouseEvent<HTMLDivElement>): number => {
    if (!timelineRef.current) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    return Math.floor(percent * totalFrames);
  }, [totalFrames]);

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggedBoundary || isDraggingPlayhead) return;
    const frame = getFrameFromMouseEvent(e);
    seekToFrame(frame);
  };

  const handleBoundaryDragStart = (phaseId: string, side: 'start' | 'end') => {
    setDraggedBoundary({ phaseId, side });
  };

  const handleBoundaryDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggedBoundary || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const frame = Math.floor(percent * totalFrames);

    setPhases(prevPhases => {
      const phaseIndex = prevPhases.findIndex(p => p.id === draggedBoundary.phaseId);
      if (phaseIndex === -1) return prevPhases;

      const newPhases = [...prevPhases];
      const phase = newPhases[phaseIndex];

      if (draggedBoundary.side === 'start') {
        const minStart = phaseIndex > 0 ? newPhases[phaseIndex - 1].startFrame + 1 : 0;
        const maxStart = phase.endFrame - 1;
        const newStart = Math.max(minStart, Math.min(maxStart, frame));

        if (phaseIndex > 0) {
          newPhases[phaseIndex - 1].endFrame = newStart - 1;
        }
        phase.startFrame = newStart;
      } else {
        const minEnd = phase.startFrame + 1;
        const maxEnd = phaseIndex < newPhases.length - 1 ? newPhases[phaseIndex + 1].endFrame - 1 : totalFrames - 1;
        const newEnd = Math.max(minEnd, Math.min(maxEnd, frame));

        phase.endFrame = newEnd;
        if (phaseIndex < newPhases.length - 1) {
          newPhases[phaseIndex + 1].startFrame = newEnd + 1;
        }
      }

      return newPhases;
    });
  };

  const handleBoundaryDragEnd = () => {
    setDraggedBoundary(null);
  };

  const handlePlayheadDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDraggingPlayhead(true);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handlePlayheadDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingPlayhead || !timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const frame = Math.floor(percent * totalFrames);
    setCurrentFrame(frame);
  };

  const handlePlayheadDragEnd = () => {
    setIsDraggingPlayhead(false);
  };

  // --- Phase CRUD ---

  const addPhase = () => {
    if (phases.length >= 10) {
      setValidationError('Maximum 10 phases allowed');
      return;
    }

    // Append the new phase to the END of the timeline and stretch it to the end
    // of the video. We carve it out of the current last phase (the last phase
    // already ends at the video end — totalFrames-1 — per the full-coverage
    // invariant). Fallback: if the last phase is a single frame and can't be
    // split, split the longest splittable phase instead so coverage stays valid.
    const lastPhase = phases[phases.length - 1];
    let splitTarget = lastPhase;
    if (lastPhase.endFrame - lastPhase.startFrame < 1) {
      for (const phase of phases) {
        if (phase.endFrame - phase.startFrame > splitTarget.endFrame - splitTarget.startFrame) {
          splitTarget = phase;
        }
      }
      if (splitTarget.endFrame - splitTarget.startFrame < 1) {
        setValidationError('Phases are too short to split — extend the timeline first.');
        return;
      }
    }

    const newPhaseId = crypto.randomUUID();

    setPhases(prevPhases => {
      const phaseIndex = prevPhases.findIndex(p => p.id === splitTarget.id);
      const target = prevPhases[phaseIndex];
      const splitPoint = Math.floor((target.startFrame + target.endFrame) / 2) + 1;
      const isLast = phaseIndex === prevPhases.length - 1;

      const newPhases = [...prevPhases];
      // Shrink the split phase to make room (no in-place mutation of prev objects).
      newPhases[phaseIndex] = { ...target, endFrame: splitPoint - 1 };
      newPhases.splice(phaseIndex + 1, 0, {
        id: newPhaseId,
        name: `Phase ${prevPhases.length + 1}`,
        startFrame: splitPoint,
        // Appending at the end → stretch to the video end so the user doesn't
        // have to drag it. (target.endFrame already == totalFrames-1 for the
        // last phase; be explicit in case it drifted.)
        endFrame: isLast ? totalFrames - 1 : target.endFrame,
        importance: 'medium',
      });

      return newPhases;
    });

    setSelectedPhaseId(newPhaseId);
    setValidationError(null);
  };

  const removePhase = (phaseId: string) => {
    if (phases.length <= 2) {
      setValidationError('Minimum 2 phases required');
      return;
    }

    setPhases(prevPhases => {
      const phaseIndex = prevPhases.findIndex(p => p.id === phaseId);
      if (phaseIndex === -1) return prevPhases;

      const newPhases = [...prevPhases];
      const removedPhase = newPhases[phaseIndex];

      if (phaseIndex > 0) {
        newPhases[phaseIndex - 1].endFrame = removedPhase.endFrame;
      } else if (phaseIndex < newPhases.length - 1) {
        newPhases[phaseIndex + 1].startFrame = removedPhase.startFrame;
      }

      newPhases.splice(phaseIndex, 1);
      return newPhases;
    });

    if (selectedPhaseId === phaseId) {
      setSelectedPhaseId(phases[0]?.id || null);
    }
    setValidationError(null);
  };

  const updatePhaseName = (phaseId: string, name: string) => {
    setPhases(prevPhases =>
      prevPhases.map(p => (p.id === phaseId ? { ...p, name } : p))
    );
  };

  const updatePhaseImportance = (phaseId: string, importance: 'low' | 'medium' | 'high' | 'critical') => {
    setPhases(prevPhases =>
      prevPhases.map(p => (p.id === phaseId ? { ...p, importance } : p))
    );
  };

  // --- Scoring config helpers ---

  const getPhaseConfig = (phaseId: string): PhaseConfig | undefined =>
    phaseConfigs.find(c => c.phaseId === phaseId);

  const togglePhaseEnabled = (phaseId: string) => {
    setPhaseConfigs(prev =>
      prev.map(c => c.phaseId === phaseId ? { ...c, enabled: !c.enabled } : c)
    );
  };

  const updatePhaseConfig = (phaseId: string, updates: Partial<PhaseConfig>) => {
    setPhaseConfigs(prev =>
      prev.map(c => c.phaseId === phaseId ? { ...c, ...updates } : c)
    );
  };

  // --- Validation ---

  const validatePhases = (): { valid: boolean; error?: string } => {
    if (phases.length < 2) {
      return { valid: false, error: 'At least 2 phases are required' };
    }

    if (phases.length > 10) {
      return { valid: false, error: 'Maximum 10 phases allowed' };
    }

    for (const phase of phases) {
      if (!phase.name?.trim()) {
        return { valid: false, error: 'All phases must have a name' };
      }
    }

    if (phases[0].startFrame !== 0) {
      return { valid: false, error: 'Phases must start at frame 0' };
    }

    if (phases[phases.length - 1].endFrame !== totalFrames - 1) {
      return { valid: false, error: 'Phases must cover the entire video' };
    }

    for (let i = 0; i < phases.length - 1; i++) {
      if (phases[i].endFrame + 1 !== phases[i + 1].startFrame) {
        return { valid: false, error: 'Phases must be continuous with no gaps' };
      }
    }

    // Warn if all phases are disabled
    const enabledCount = phaseConfigs.filter(c => c.enabled).length;
    if (enabledCount === 0) {
      return { valid: false, error: 'At least one phase must be enabled for scoring.' };
    }

    return { valid: true };
  };

  const handleNext = () => {
    const validation = validatePhases();
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid phase configuration');
      return;
    }
    setValidationError(null);
    onNext();
  };

  // --- Formatting helpers ---

  const formatTime = (frame: number) => {
    const seconds = frame / fps;
    return `${seconds.toFixed(2)}s`;
  };

  const getPhaseColor = (index: number) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-cyan-500',
    ];
    return colors[index % colors.length];
  };

  // --- Render ---

  return (
    <Card className="border-2 border-accent/50">
      <CardHeader className="bg-accent/10">
        <CardTitle className="flex items-center gap-2">
          <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Step 2: Define Movement Phases &amp; Scoring
        </CardTitle>
        <p className="text-sm text-text-secondary mt-2">
          Break down the movement into distinct phases, then configure how each is scored.
          Drag the timeline boundaries to adjust timing.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {validationError && (
          <Alert variant="error">{validationError}</Alert>
        )}

        <Alert variant="info">
          Define at least 2 phases (e.g., "Preparation", "Execution", "Follow-through").
          Use the scoring section on each phase card to configure importance and weight.
        </Alert>

        {/* Video Player */}
        <div className="bg-black rounded-lg overflow-hidden aspect-video">
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full"
            playsInline
          />
        </div>

        {/* Video Controls */}
        <div className="flex items-center gap-2">
          <Button
            onClick={togglePlayPause}
            variant="outline"
            size="sm"
          >
            {isPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </Button>
          <div className="flex-1 text-sm text-text-secondary">
            Frame {currentFrame} / {totalFrames - 1} ({formatTime(currentFrame)})
          </div>
        </div>

        {/* Timeline with Phase Markers */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-text-secondary">Phase Timeline</label>
          <div
            ref={timelineRef}
            className="relative h-16 bg-dark-elevated-2 rounded cursor-pointer select-none"
            onClick={handleTimelineClick}
            onMouseMove={draggedBoundary ? handleBoundaryDrag : (isDraggingPlayhead ? handlePlayheadDrag : undefined)}
            onMouseUp={() => { handleBoundaryDragEnd(); handlePlayheadDragEnd(); }}
            onMouseLeave={() => { handleBoundaryDragEnd(); handlePlayheadDragEnd(); }}
          >
            {/* Phase segments */}
            {phases.map((phase, index) => {
              const startPercent = (phase.startFrame / totalFrames) * 100;
              const widthPercent = ((phase.endFrame - phase.startFrame + 1) / totalFrames) * 100;
              const isSelected = phase.id === selectedPhaseId;
              const config = getPhaseConfig(phase.id);
              const isEnabled = config?.enabled ?? true;

              return (
                <div
                  key={phase.id}
                  className={`absolute top-0 h-full ${getPhaseColor(index)} ${
                    isSelected ? 'opacity-90 ring-2 ring-blue-600' : 'opacity-70'
                  } ${!isEnabled ? 'opacity-30 grayscale' : ''} hover:opacity-100 transition-opacity`}
                  style={{
                    left: `${startPercent}%`,
                    width: `${widthPercent}%`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPhaseId(phase.id);
                  }}
                >
                  {/* Phase label */}
                  <div className="absolute top-1 left-1 text-xs font-bold text-white truncate px-1">
                    {phase.name}
                  </div>

                  {/* Boundary handles */}
                  {index > 0 && (
                    <div
                      className="absolute left-0 top-0 w-1 h-full bg-white cursor-ew-resize hover:w-2 transition-all"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleBoundaryDragStart(phase.id, 'start');
                      }}
                    />
                  )}
                  <div
                    className="absolute right-0 top-0 w-1 h-full bg-white cursor-ew-resize hover:w-2 transition-all"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleBoundaryDragStart(phase.id, 'end');
                    }}
                  />
                </div>
              );
            })}

            {/* Current frame indicator - draggable playhead */}
            <div
              className="absolute top-0 w-0.5 h-full bg-red-500 z-10"
              style={{ left: `${(currentFrame / totalFrames) * 100}%` }}
            >
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-red-500 rounded-full cursor-grab active:cursor-grabbing hover:scale-110 transition-transform shadow-md"
                onMouseDown={handlePlayheadDragStart}
              />
            </div>
          </div>
          <p className="text-xs text-text-muted">
            Drag red playhead to scrub. Click to seek. Drag white boundaries to adjust phases.
          </p>
        </div>

        {/* Phase List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-text-secondary">
              Phases ({phases.length})
            </label>
            <Button
              onClick={addPhase}
              variant="outline"
              size="sm"
              disabled={phases.length >= 10}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Phase
            </Button>
          </div>

          <div className="space-y-2 max-h-[680px] overflow-y-auto">
            {phases.map((phase, index) => {
              const isSelected = phase.id === selectedPhaseId;
              const config = getPhaseConfig(phase.id);
              const isEnabled = config?.enabled ?? true;

              return (
                <div
                  key={phase.id}
                  className={`border rounded-lg p-3 transition-colors ${
                    isSelected ? 'border-accent/60 bg-accent/10' : 'border-border-subtle bg-dark-elevated-1'
                  } ${!isEnabled ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedPhaseId(phase.id)}
                >
                  {/* Row 1: color swatch + name input + enabled toggle + delete */}
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded flex-shrink-0 ${getPhaseColor(index)}`} />
                    <Input
                      value={phase.name}
                      onChange={(e) => updatePhaseName(phase.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Phase name"
                      className="flex-1"
                    />
                    {/* Enabled toggle */}
                    <button
                      type="button"
                      title={isEnabled ? 'Disable phase for scoring' : 'Enable phase for scoring'}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePhaseEnabled(phase.id);
                      }}
                      className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${
                        isEnabled ? 'bg-accent' : 'bg-dark-elevated-2'
                      }`}
                    >
                      <div
                        className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200"
                        style={{ left: isEnabled ? '1.25rem' : '0.125rem' }}
                      />
                    </button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePhase(phase.id);
                      }}
                      variant="outline"
                      size="sm"
                      disabled={phases.length <= 2}
                      className="text-danger hover:text-danger hover:bg-danger/10 flex-shrink-0"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>

                  {/* Row 2: Importance selector */}
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs font-medium text-text-muted whitespace-nowrap">
                      Importance:
                    </label>
                    <div className="flex gap-1">
                      {(['low', 'medium', 'high', 'critical'] as const).map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePhaseImportance(phase.id, level);
                          }}
                          className={`px-2 py-1 text-xs rounded border transition-all ${
                            phase.importance === level
                              ? getImportanceBadgeClasses(level) + ' font-semibold'
                              : 'bg-dark-elevated-2 text-text-secondary border-border-subtle hover:bg-dark-elevated-1'
                          }`}
                        >
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Row 3: Frame range */}
                  <div className="mt-1 text-xs text-text-muted">
                    Frames {phase.startFrame} - {phase.endFrame} ({formatTime(phase.startFrame)} - {formatTime(phase.endFrame)})
                    · Duration: {((phase.endFrame - phase.startFrame + 1) / fps).toFixed(2)}s
                  </div>

                  {/* Expanded scoring config (only when this phase is selected) */}
                  {isSelected && (
                    <div className="mt-3 pt-3 border-t border-border-subtle space-y-3" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Scoring Configuration</p>

                      {/* Weight slider */}
                      {isEnabled && (
                        <div className="flex items-center gap-3">
                          <label className="text-xs text-text-muted w-12 flex-shrink-0">Weight</label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Math.round((config?.weight ?? 0.5) * 100)}
                            onChange={(e) => updatePhaseConfig(phase.id, { weight: parseInt(e.target.value) / 100 })}
                            className="flex-1 h-1.5 rounded-full accent-accent cursor-pointer"
                          />
                          <span className="text-xs font-mono text-accent w-9 text-right">
                            {Math.round((config?.weight ?? 0.5) * 100)}%
                          </span>
                        </div>
                      )}

                      {/* Disabled phase notice */}
                      {!isEnabled && (
                        <p className="text-xs text-text-muted italic">
                          This phase is disabled and will be skipped during scoring. Toggle the switch above to enable it.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mirror-invariant scoring is now always enabled — no toggle needed */}

        {/* Navigation */}
        <div className="flex gap-2 pt-4 border-t border-border-subtle">
          <Button
            variant="outline"
            onClick={onBack}
          >
            Back to Auto-Detect
          </Button>
          <Button
            onClick={handleNext}
            disabled={phases.length < 2 || phases.some(p => !p.name?.trim())}
            className="flex-1 bg-accent hover:bg-accent-hover text-text-inverse font-bold"
          >
            Continue to Scoring Studio ({phases.length} phases defined)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
